import { cn } from "@/lib/utils";
import { CreditCard, Layers, type LucideIcon, Puzzle, ShoppingBag, Utensils } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/**
 * Shown while a live device boots. The wait is long enough to read one comparison
 * at a time; the lines stay inside the product's own pitch (one platform, commission-free
 * ordering, a branded app) and do not quote fees we have not published.
 */
const HOLD_MS = 5500;

const FACTS: { vs: string; line: string; icon: LucideIcon; tint: string }[] = [
  {
    vs: "Toast",
    icon: Utensils,
    tint: "bg-[#ff8a3d]",
    line: "Toast is a restaurant register tied to its own hardware and ordering. MyManager is that register, plus your website, your app, marketing and the customer list — one company, one bill.",
  },
  {
    vs: "Square",
    icon: CreditCard,
    tint: "bg-[#7c8cff]",
    line: "Square splits the register, appointments, the online store and marketing into separate products. MyManager runs them on one login and one customer list.",
  },
  {
    vs: "Shopify",
    icon: ShoppingBag,
    tint: "bg-[#3dd68c]",
    line: "Shopify is a storefront. Kitchen screens, kiosks, tableside service and a branded guest app are other vendors. Those screens are already part of MyManager.",
  },
  {
    vs: "Clover",
    icon: Puzzle,
    tint: "bg-[#5dce7a]",
    line: "Clover grows by adding third-party apps that do not share one customer list. Ordering, loyalty, marketing and the register already talk to each other in MyManager.",
  },
  {
    vs: "a stack of apps",
    icon: Layers,
    tint: "bg-[#5b9dff]",
    line: "Most owners pay 6–10 vendors for a site, ordering, a POS, loyalty, email and a custom app. In MyManager you add a feature by turning it on, not by signing another contract.",
  },
];

function MyManager() {
  return (
    <strong className="animate-shimmer bg-gradient-to-r from-white via-[#9ec5ff] to-white bg-[length:200%_100%] bg-clip-text font-bold text-transparent">
      MyManager
    </strong>
  );
}

function FactLine({ line }: { line: string }) {
  const parts = line.split("MyManager");
  return (
    <p className="break-words px-2.5 pb-2 pt-1.5 text-[11px] leading-snug text-white/95">
      {parts.map((part, n) => (
        <span key={`${n}-${part.slice(0, 12)}`}>
          {part}
          {n < parts.length - 1 ? <MyManager /> : null}
        </span>
      ))}
    </p>
  );
}

function FactCard({
  fact,
  motion,
}: {
  fact: (typeof FACTS)[number];
  motion: "in" | "out" | "still";
}) {
  const Icon = fact.icon;
  return (
    <div
      className={cn(
        "w-full min-w-0 max-w-full rounded-xl bg-[#162033] text-left",
        motion === "in" && "animate-fact-in",
        motion === "out" && "animate-fact-out",
      )}
    >
      <div className="flex items-center gap-2 px-2.5 pt-2">
        <span
          className={cn(
            "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white shadow-sm",
            motion === "in" && "animate-icon-pop",
            fact.tint,
          )}
        >
          <Icon className="h-3.5 w-3.5" />
        </span>
        <p className="min-w-0 text-[10px] font-semibold uppercase leading-tight tracking-wide text-white/70">
          Switching from {fact.vs}
        </p>
      </div>
      <FactLine line={fact.line} />
      <div className="h-0.5 overflow-hidden rounded-b-xl bg-white/10">
        {motion !== "out" ? (
          <div className="h-full origin-left animate-fact-progress bg-white/80" />
        ) : null}
      </div>
    </div>
  );
}

export function SwitchFactsLoader() {
  const [i, setI] = useState(0);
  const [leaving, setLeaving] = useState<number | null>(null);
  const index = useRef(0);
  useEffect(() => {
    const t = setInterval(() => {
      const current = index.current;
      const next = (current + 1) % FACTS.length;
      index.current = next;
      setLeaving(current);
      setI(next);
    }, HOLD_MS);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (leaving === null) return;
    const t = setTimeout(() => setLeaving(null), 500);
    return () => clearTimeout(t);
  }, [leaving]);
  const fact = FACTS[i];
  const gone = leaving === null ? undefined : FACTS[leaving];
  if (!fact) return null;
  return (
    <div className="flex w-full min-w-0 max-w-[340px] flex-col items-center gap-1.5 px-0.5 text-center text-white">
      <div className="h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-white/25 border-t-white" />
      <div className="min-w-0">
        <p className="text-[13px] font-semibold leading-tight">Starting this device</p>
        <p className="mt-0.5 text-[10px] text-white/70">This usually takes under a minute</p>
      </div>
      <div className="grid w-full min-w-0 overflow-hidden [&>*]:col-start-1 [&>*]:row-start-1">
        {gone ? <FactCard fact={gone} motion="out" /> : null}
        <FactCard key={fact.vs} fact={fact} motion={gone ? "in" : "still"} />
      </div>
      <div className="flex items-center gap-1.5" aria-hidden>
        {FACTS.map((f, n) => (
          <span
            key={f.vs}
            className={cn(
              "h-1.5 rounded-full transition-all duration-300",
              n === i ? "w-4 bg-white" : "w-1.5 bg-white/30",
            )}
          />
        ))}
      </div>
    </div>
  );
}
